import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

interface FormatDurationOptions {
  showSeconds?: boolean;
  compact?: boolean;
}

/**
 * Форматирует длительность в миллисекундах в человекочитаемую строку.
 *
 * Использует dayjs с плагином duration
 * для корректного разбиения времени на часы, минуты и секунды.
 *
 * Формат вывода:
 * - `Xч Yм Zс` (например: "2ч 15м 10с")
 * - если часов нет → `Yм Zс`
 * - если меньше минуты и showSeconds: false → `<1м`
 * - секунды отображаются опционально
 *
 * @param {number} ms - Длительность в миллисекундах.
 * @param {FormatDurationOptions} [options] - Опции форматирования.
 * @param {boolean} [options.showSeconds=true]
 *  Определяет, отображать ли секунды в результате.
 *
 * @returns {string} Отформатированная строка длительности.
 *
 * @example
 * formatDuration(7265000)
 * // "2ч 1м 5с"
 *
 * @example
 * formatDuration(45000, { showSeconds: false })
 * // "<1м"
 *
 * @remarks
 * - Значение округляется вниз (floor) до целых часов/минут/секунд.
 * - Не учитывает дни отдельно — часы могут превышать 24.
 */
export const formatDuration = (ms: number | null | undefined, options?: FormatDurationOptions) => {
  const { showSeconds = true, compact = false } = options || {};

  const d = dayjs.duration(Math.abs(ms || 0));

  const h = Math.floor(d.asHours());
  const m = d.minutes();
  const s = d.seconds();

  if (compact) {
    if (h >= 48) return `${Math.floor(h / 24)}д`;
    if (h >= 24) return `${h}ч`;
  }

  return `${h ? h + 'ч' : ''} ${m ? m + 'м' : !h && !showSeconds ? '<1м' : ''} ${s && showSeconds ? s + 'с' : ''}`;
};
